import { useState, useMemo, useEffect, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PanelContent from '@shared/components/PanelContent';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import { useMapStore } from '@shared/hooks/useMapStore';
import { useBasket } from '@features/basket/hooks/useBasket';
import {
  usePeriodStore,
  resolveEffectivePeriod,
  resolveEffectivePrice,
  hasValidPricing,
} from '@shared/pricing';

import { useRoute } from '../hooks/useRoute';
import { useRouteProducts } from '../hooks/useRouteProducts';
import RouteProductList from './RouteProductList';
import RouteProductFilter, { type ProductFilters } from './RouteProductFilter';
import type { RouteProduct } from '../types';

interface Props {
  routeId: string;
  onBack: () => void;
  onEdit: () => void;
}

export default function ActiveRoutePanel({ routeId, onBack, onEdit }: Props) {
  const { data: route, isLoading: routeLoading, error: routeError, refetch } = useRoute(routeId);
  const { data: products, isLoading: productsLoading } = useRouteProducts(routeId);
  const setFilteredProductIds = useMapStore((s) => s.setFilteredProductIds);
  const addItem = useBasket((s) => s.addItem);
  const requestedPeriod = usePeriodStore((s) => s.requestedPeriodMonths);
  const setRequestedPeriod = usePeriodStore((s) => s.setRequestedPeriodMonths);

  const [filters, setFilters] = useState<ProductFilters>({
    searchQuery: '',
    productType: '',
    holdingStatuses: [],
    coverageValues: [],
    routeFilter: '',
    basketFilter: '',
  });

  // Apply filters to products
  const filteredProducts = useMemo(() => {
    if (!products) return [];

    const filtered = products.filter((product: RouteProduct) => {
      // Filter by search query
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const nameMatch = product.name.toLowerCase().includes(query);
        const cellIdMatch = product.cellId?.toLowerCase().includes(query);
        if (!nameMatch && !cellIdMatch) {
          return false;
        }
      }

      // Filter by product type
      if (filters.productType && filters.productType !== product.productType) {
        return false;
      }

      // Filter by holding status
      if (
        filters.holdingStatuses.length > 0 &&
        !filters.holdingStatuses.includes(product.holdingStatus)
      ) {
        return false;
      }

      // Filter by coverage
      if (
        filters.coverageValues.length > 0 &&
        (!product.coverage || !filters.coverageValues.includes(product.coverage))
      ) {
        return false;
      }

      // Filter by route membership
      if (filters.routeFilter === 'onRoute' && !product.isRequired) {
        return false;
      }
      if (filters.routeFilter === 'offRoute' && product.isRequired) {
        return false;
      }

      // Filter by basket status
      if (filters.basketFilter === 'inBasket' && !product.inBasket) {
        return false;
      }
      if (filters.basketFilter === 'notInBasket' && product.inBasket) {
        return false;
      }

      return true;
    });

    return filtered;
  }, [products, filters]);

  // Count active filters
  const activeFilterCount =
    (filters.searchQuery ? 1 : 0) +
    (filters.productType ? 1 : 0) +
    filters.holdingStatuses.length +
    filters.coverageValues.length +
    (filters.routeFilter ? 1 : 0) +
    (filters.basketFilter ? 1 : 0);

  // Filtered products that can be added to basket (not active, not already in basket, have valid pricing)
  const addableProducts = useMemo(() => {
    return filteredProducts.filter(
      (p) => p.holdingStatus !== 'active' && !p.inBasket && hasValidPricing(p.prices),
    );
  }, [filteredProducts]);

  const handleAddMissingToBasket = useCallback(() => {
    for (const product of addableProducts) {
      if (!hasValidPricing(product.prices)) continue;
      const effectivePeriod = resolveEffectivePeriod(product.prices, requestedPeriod);
      const effectivePrice = resolveEffectivePrice(product.prices, requestedPeriod);
      addItem({
        productId: product.productId,
        productType: product.productType,
        name: product.name,
        title: product.title,
        coverage: product.coverage,
        licensePeriodMonths: effectivePeriod,
        priceAmount: effectivePrice?.priceAmount,
        currencyCode: effectivePrice?.currencyCode,
        availablePrices: product.prices,
        source: 'route',
        geometry: product.geometry,
      });
    }
  }, [addableProducts, addItem, requestedPeriod]);

  // Update filtered product IDs in map store whenever filtered products change
  useEffect(() => {
    const productIds = filteredProducts.map((p) => p.productId);
    setFilteredProductIds(productIds);

    return () => {
      // Clear filtered product IDs when component unmounts
      setFilteredProductIds(null);
    };
  }, [filteredProducts, setFilteredProductIds]);

  if (routeLoading) return <PanelSkeleton />;

  if (routeError || !route) {
    return (
      <RetryableError
        message={routeError?.message ?? 'Failed to load route'}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <PanelContent>
      <div data-testid="active-route-panel" className="flex flex-col gap-3">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={onBack} aria-label="Back to route list">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {route.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {route.length.toFixed(1)} NM &middot; {route.waypointCount} waypoints
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onEdit}
            aria-label="Edit route"
            sx={{ color: 'text.secondary' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>

        <RouteProductFilter
          filters={filters}
          onFiltersChange={setFilters}
          activeFilterCount={activeFilterCount}
          periodValue={requestedPeriod}
          onPeriodChange={setRequestedPeriod}
        />

        {addableProducts.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddShoppingCartIcon />}
            onClick={handleAddMissingToBasket}
            sx={{ textTransform: 'none' }}
          >
            Add {addableProducts.length} to order
          </Button>
        )}

        <RouteProductList products={filteredProducts} loading={productsLoading} />
      </div>
    </PanelContent>
  );
}
